import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Reporter, SimilarReporter, SavedSearch } from '@pf/shared';
import {
  useReportersInfinite,
  useReportersSearchInfinite,
  useReporterBeats,
  useReporterSimilar,
} from '../hooks/useReporters';
import { usePublicationsSearch, PublicationOption } from '../hooks/usePublications';
import { ImportReporterModal, BatchImportModal } from '../components/ImportReporterModal';
import { AddToPressListModal } from '../components/AddToPressListModal';
import { useSavedSearches, useCreateSavedSearch, useDeleteSavedSearch } from '../hooks/useSavedSearches';

// ── Utilities ─────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return (
    (parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')
  ).toUpperCase();
}

// ── Highlight ─────────────────────────────────────────────────────────────────

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query || query.length < 2) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-100 text-gray-900 rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

// ── SimilarityBadge ────────────────────────────────────────────────────────────

function SimilarityBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const colorClass =
    pct >= 80
      ? 'bg-green-100 text-green-700'
      : pct >= 60
        ? 'bg-blue-100 text-blue-700'
        : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {pct}% match
    </span>
  );
}

// ── PublicationCombobox ───────────────────────────────────────────────────────

function PublicationCombobox({
  selectedId,
  selectedName,
  onChange,
}: {
  selectedId: string | undefined;
  selectedName: string;
  onChange: (id: string | undefined, name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 300);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = usePublicationsSearch(debouncedInput);
  const results: PublicationOption[] = data?.data ?? [];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (selectedId) {
    return (
      <button
        onClick={() => onChange(undefined, '')}
        className="flex items-center gap-1.5 h-8 px-3 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
      >
        <span className="max-w-[140px] truncate">{selectedName}</span>
        <span className="text-blue-400 hover:text-blue-600 text-sm leading-none">×</span>
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 h-8 px-3 text-xs border border-gray-300 rounded-full hover:border-gray-400 bg-white text-gray-700 transition-colors"
      >
        Publication
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1.5 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-72">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search publications..."
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {isLoading && (
              <div className="py-3 text-center text-xs text-gray-400">Searching...</div>
            )}
            {!isLoading && inputValue && results.length === 0 && (
              <div className="py-3 text-center text-xs text-gray-400">No publications found</div>
            )}
            {!inputValue && (
              <div className="py-3 text-center text-xs text-gray-400">Type to search publications</div>
            )}
            {results.map((pub) => (
              <button
                key={pub.id}
                onClick={() => { onChange(pub.id, pub.name); setOpen(false); setInputValue(''); }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900 truncate block">{pub.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── BeatFilter ────────────────────────────────────────────────────────────────

function BeatFilter({ selected, onChange }: { selected: string[]; onChange: (beats: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data } = useReporterBeats();
  const beats = data?.beats ?? [];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggleBeat(beat: string) {
    onChange(selected.includes(beat) ? selected.filter((b) => b !== beat) : [...selected, beat]);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 h-8 px-3 text-xs border rounded-full transition-colors ${
          selected.length > 0
            ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
            : 'border-gray-300 hover:border-gray-400 bg-white text-gray-700'
        }`}
      >
        {selected.length > 0 ? `Beat (${selected.length})` : 'Beat'}
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1.5 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-56 max-h-64 overflow-y-auto">
          {beats.length === 0 ? (
            <div className="py-3 text-center text-xs text-gray-400">No beats available</div>
          ) : (
            beats.map((beat) => (
              <label key={beat} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(beat)}
                  onChange={() => toggleBeat(beat)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600"
                />
                <span className="text-xs text-gray-700 truncate">{beat}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── ArticleFrequencyFilter ─────────────────────────────────────────────────────

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily (20+/mo)',
  weekly: 'Weekly (4+/mo)',
  monthly: 'Monthly (1+/mo)',
};

function ArticleFrequencyFilter({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (v: 'daily' | 'weekly' | 'monthly' | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 h-8 px-3 text-xs border rounded-full transition-colors ${
          value
            ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
            : 'border-gray-300 hover:border-gray-400 bg-white text-gray-700'
        }`}
      >
        {value ? FREQUENCY_LABELS[value] : 'Frequency'}
        {value ? (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(undefined); setOpen(false); }}
            className="text-blue-400 hover:text-blue-600 text-sm leading-none ml-0.5"
          >
            ×
          </span>
        ) : (
          <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      {open && (
        <div className="absolute top-full mt-1.5 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-48">
          {(['daily', 'weekly', 'monthly'] as const).map((freq) => (
            <button
              key={freq}
              onClick={() => { onChange(freq); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors ${
                value === freq ? 'font-medium text-blue-700 bg-blue-50' : 'text-gray-700'
              }`}
            >
              {FREQUENCY_LABELS[freq]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ReporterCard ──────────────────────────────────────────────────────────────

function ReporterCard({
  reporter,
  searchQuery,
  onImport,
  onAddToList,
  onMoreLikeThis,
  selected,
  onSelectToggle,
  selectionMode,
  focused,
  cardRef,
}: {
  reporter: Reporter;
  searchQuery: string;
  onImport: (reporter: Reporter) => void;
  onAddToList: (reporter: Reporter) => void;
  onMoreLikeThis: (reporter: Reporter) => void;
  selected: boolean;
  onSelectToggle: (id: string) => void;
  selectionMode: boolean;
  focused: boolean;
  cardRef?: React.RefCallback<HTMLDivElement>;
}) {
  const navigate = useNavigate();
  const topBeats = reporter.beats.slice(0, 3);
  const inContacts = !!reporter.userContactId;
  const hasSimilarity = typeof reporter.similarityScore === 'number' && reporter.similarityScore > 0;

  function handleCardClick() {
    if (selectionMode) {
      onSelectToggle(reporter.id);
    } else {
      navigate(`/reporters/${reporter.id}`);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!selectionMode) navigate(`/reporters/${reporter.id}`);
      else onSelectToggle(reporter.id);
    }
  }

  return (
    <div
      ref={cardRef}
      tabIndex={0}
      role="article"
      aria-label={`${reporter.name}${reporter.primaryPublication?.name ? `, ${reporter.primaryPublication.name}` : ''}`}
      className={`group bg-white rounded-lg border transition-all cursor-pointer relative outline-none ${
        selected
          ? 'border-blue-400 ring-2 ring-blue-200 shadow-sm'
          : focused
            ? 'border-blue-300 ring-2 ring-blue-100 shadow-sm'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm focus-visible:border-blue-300 focus-visible:ring-2 focus-visible:ring-blue-100'
      }`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
    >
      {/* Selection checkbox */}
      <div
        className="absolute top-3 left-3 z-10"
        onClick={(e) => { e.stopPropagation(); onSelectToggle(reporter.id); }}
      >
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            selected
              ? 'bg-blue-600 border-blue-600'
              : 'bg-white border-gray-300 opacity-0 group-hover:opacity-100'
          } ${selectionMode ? 'opacity-100' : ''}`}
        >
          {selected && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>

      <div className="p-4">
        {/* Header: avatar + name + similarity badge */}
        <div className="flex items-start gap-3">
          <div className={selectionMode || selected ? 'pl-6' : ''}>
            {reporter.imageUrl ? (
              <img
                src={reporter.imageUrl}
                alt={reporter.name}
                className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm flex items-center justify-center flex-shrink-0 select-none">
                {getInitials(reporter.name)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-1">
              <h3 className="text-sm font-semibold text-gray-900 leading-tight truncate">
                <Highlight text={reporter.name} query={searchQuery} />
              </h3>
              {hasSimilarity && (
                <div className="flex-shrink-0">
                  <SimilarityBadge score={reporter.similarityScore!} />
                </div>
              )}
            </div>
            {reporter.title && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{reporter.title}</p>
            )}
            {reporter.primaryPublication?.name && (
              <p className="text-xs text-blue-600 mt-0.5 truncate">{reporter.primaryPublication.name}</p>
            )}
          </div>
        </div>

        {/* Beat tags */}
        {topBeats.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {topBeats.map((beat) => (
              <span
                key={beat}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
              >
                {beat}
              </span>
            ))}
            {reporter.beats.length > 3 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                +{reporter.beats.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Latest article */}
        {reporter.latestArticleTitle && (
          <div className="mt-2.5">
            <a
              href={reporter.latestArticleUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-gray-500 hover:text-blue-600 line-clamp-2 leading-snug transition-colors"
              title={reporter.latestArticleTitle}
            >
              <span className="text-gray-400 mr-1">📰</span>
              {reporter.latestArticleTitle}
            </a>
          </div>
        )}

        {/* Footer: email + actions */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between gap-2">
            {reporter.email ? (
              <span className="text-xs text-gray-400 truncate min-w-0" title={reporter.email}>
                {reporter.email}
              </span>
            ) : (
              <span className="text-xs text-gray-300">No email</span>
            )}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* More like this */}
              <button
                onClick={(e) => { e.stopPropagation(); onMoreLikeThis(reporter); }}
                className="text-xs text-gray-500 hover:text-blue-600 px-1.5 py-1 rounded hover:bg-blue-50 transition-colors"
                title="Find similar reporters"
                aria-label="Find reporters similar to this one"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              {/* Add to press list */}
              <button
                onClick={(e) => { e.stopPropagation(); onAddToList(reporter); }}
                className="text-xs text-gray-500 hover:text-purple-600 px-1.5 py-1 rounded hover:bg-purple-50 transition-colors"
                title="Add to press list"
                aria-label="Add to press list"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>
              {/* Add to contacts / In contacts */}
              {inContacts ? (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${reporter.userContactId}`); }}
                  className="text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded font-medium flex items-center gap-1 hover:bg-green-100 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  In Contacts
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); onImport(reporter); }}
                  className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-2.5 py-1 rounded font-medium text-gray-700 transition-colors"
                >
                  + Contacts
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SimilarReporterCard ────────────────────────────────────────────────────────

function SimilarReporterCard({
  reporter,
  onImport,
  onAddToList,
  onMoreLikeThis,
}: {
  reporter: SimilarReporter;
  onImport: (r: Reporter) => void;
  onAddToList: (r: Reporter) => void;
  onMoreLikeThis: (r: Reporter) => void;
}) {
  const navigate = useNavigate();
  const topBeats = reporter.beats.slice(0, 3);

  function asReporter(): Reporter {
    return {
      id: reporter.id,
      name: reporter.name,
      firstName: null,
      lastName: null,
      email: null,
      emailConfidence: null,
      url: null,
      title: reporter.title,
      bio: null,
      imageUrl: reporter.imageUrl,
      twitter: null,
      linkedin: null,
      beats: reporter.beats,
      location: null,
      enrichmentStatus: 'complete',
      primaryPublication: reporter.primaryPublication
        ? { id: reporter.primaryPublication.id, name: reporter.primaryPublication.name ?? '', url: '', description: null, faviconUrl: reporter.primaryPublication.faviconUrl, categories: [], emailFormats: null, reportersCount: 0, pageRank: null, monthlyArticleCount: null, audienceSizeEstimate: null, audienceMetricsUpdatedAt: null }
        : null,
      publications: [],
      userContactId: null,
      similarityScore: reporter.similarityScore,
      createdAt: '',
      updatedAt: '',
    };
  }

  return (
    <div
      tabIndex={0}
      role="article"
      aria-label={reporter.name}
      className="group bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer outline-none focus-visible:border-blue-300 focus-visible:ring-2 focus-visible:ring-blue-100"
      onClick={() => navigate(`/reporters/${reporter.id}`)}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/reporters/${reporter.id}`); }}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div>
            {reporter.imageUrl ? (
              <img
                src={reporter.imageUrl}
                alt={reporter.name}
                className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm flex items-center justify-center flex-shrink-0 select-none">
                {getInitials(reporter.name)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-1">
              <h3 className="text-sm font-semibold text-gray-900 leading-tight truncate">{reporter.name}</h3>
              <SimilarityBadge score={reporter.similarityScore} />
            </div>
            {reporter.title && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{reporter.title}</p>
            )}
            {reporter.primaryPublication?.name && (
              <p className="text-xs text-blue-600 mt-0.5 truncate">{reporter.primaryPublication.name}</p>
            )}
          </div>
        </div>
        {topBeats.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {topBeats.map((beat) => (
              <span key={beat} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                {beat}
              </span>
            ))}
            {reporter.beats.length > 3 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                +{reporter.beats.length - 3}
              </span>
            )}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-end gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onMoreLikeThis(asReporter()); }}
            className="text-xs text-gray-500 hover:text-blue-600 px-1.5 py-1 rounded hover:bg-blue-50 transition-colors"
            title="Find similar reporters"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onAddToList(asReporter()); }}
            className="text-xs text-gray-500 hover:text-purple-600 px-1.5 py-1 rounded hover:bg-purple-50 transition-colors"
            title="Add to press list"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onImport(asReporter()); }}
            className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-2.5 py-1 rounded font-medium text-gray-700 transition-colors"
          >
            + Contacts
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ReportersPage ─────────────────────────────────────────────────────────────

export default function ReportersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importTarget, setImportTarget] = useState<Reporter | null>(null);
  const [addToListTarget, setAddToListTarget] = useState<Reporter | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchError, setSaveSearchError] = useState('');
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const savedDropdownRef = useRef<HTMLDivElement>(null);
  const { data: savedSearchesData } = useSavedSearches('reporter');
  const savedSearches = savedSearchesData?.savedSearches ?? [];
  const createSavedSearch = useCreateSavedSearch();
  const deleteSavedSearch = useDeleteSavedSearch();

  // ── URL params ──────────────────────────────────────────────────────────────
  const q = searchParams.get('q') ?? '';
  const publicationId = searchParams.get('publicationId') ?? undefined;
  const publicationName = searchParams.get('publicationName') ?? '';
  const beatParam = searchParams.get('beat') ?? '';
  const selectedBeats = beatParam ? beatParam.split(',').filter(Boolean) : [];
  const hasEmailParam = searchParams.get('hasEmail');
  const hasEmail = hasEmailParam === 'true' ? true : hasEmailParam === 'false' ? false : undefined;
  const location = searchParams.get('location') ?? undefined;
  const articleFrequency = (searchParams.get('articleFrequency') ?? undefined) as 'daily' | 'weekly' | 'monthly' | undefined;
  const similarTo = searchParams.get('similarTo') ?? undefined;
  const similarName = searchParams.get('similarName') ?? '';

  // ── Local search input synced to URL ────────────────────────────────────────
  const [searchInput, setSearchInput] = useState(q);
  const debouncedSearch = useDebounce(searchInput, 400);

  useEffect(() => {
    const currentQ = searchParams.get('q') ?? '';
    if (debouncedSearch === currentQ) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('q', debouncedSearch);
        else next.delete('q');
        // Clear similarTo when starting a new search
        next.delete('similarTo');
        next.delete('similarName');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch]);

  function updateFilter(key: string, value: string | undefined) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  // ── Data fetching ───────────────────────────────────────────────────────────
  const sharedFilterParams = {
    publicationId,
    beat: beatParam || undefined,
    hasEmail,
    location,
    articleFrequency,
  };

  const isSmartSearch = !!q && !similarTo;

  // Smart (vector/hybrid) search when query is present
  const searchQuery = useReportersSearchInfinite({
    q: q || '',
    ...sharedFilterParams,
    enabled: isSmartSearch,
  });

  // Basic paginated list when no query
  const listQuery = useReportersInfinite(
    isSmartSearch || similarTo ? {} : sharedFilterParams,
  );

  // Similar reporters mode
  const similarQuery = useReporterSimilar(similarTo);

  // Active query result
  const activeQuery = isSmartSearch ? searchQuery : !similarTo ? listQuery : null;
  const allReporters: Reporter[] = activeQuery?.data?.pages.flatMap((p) => p.data) ?? [];
  const total = activeQuery?.data?.pages[0]?.total ?? 0;
  const isLoading = activeQuery ? activeQuery.isLoading : similarQuery.isLoading;
  const isError = activeQuery ? activeQuery.isError : similarQuery.isError;
  const similarReporters = similarTo ? (similarQuery.data?.data ?? []) : [];
  const hasNextPage = activeQuery?.hasNextPage ?? false;
  const isFetchingNextPage = activeQuery?.isFetchingNextPage ?? false;
  const fetchNextPage = activeQuery?.fetchNextPage ?? (() => {});

  // Search mode label for UI
  const searchModePages = isSmartSearch ? (searchQuery.data?.pages ?? []) : [];
  const searchMode = searchModePages.length > 0
    ? (searchModePages[0] as { searchMode?: string }).searchMode
    : undefined;

  const activeFilterCount = [
    !!publicationId,
    selectedBeats.length > 0,
    hasEmail !== undefined,
    !!location,
    !!articleFrequency,
  ].filter(Boolean).length;

  // ── Keyboard navigation ─────────────────────────────────────────────────────
  const displayCount = similarTo ? similarReporters.length : allReporters.length;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (e.key === 'Escape') {
        setFocusedIndex(-1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => {
          const next = Math.min(i + 1, displayCount - 1);
          cardRefs.current[next]?.focus();
          return next;
        });
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => {
          const prev = Math.max(i - 1, 0);
          cardRefs.current[prev]?.focus();
          return prev;
        });
      }
    },
    [displayCount],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset focus when results change
  useEffect(() => {
    setFocusedIndex(-1);
    cardRefs.current = [];
  }, [q, publicationId, beatParam, hasEmail, location, articleFrequency, similarTo]);

  // ── Selection ───────────────────────────────────────────────────────────────
  function handleSelectToggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSelectAll() {
    setSelectedIds(new Set(allReporters.map((r) => r.id)));
  }

  function handleClearSelection() {
    setSelectedIds(new Set());
  }

  const selectionMode = selectedIds.size > 0;
  const selectedReporters = allReporters.filter((r) => selectedIds.has(r.id));

  // ── "More like this" ────────────────────────────────────────────────────────
  function handleMoreLikeThis(reporter: Reporter) {
    setSearchInput('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('q');
        next.set('similarTo', reporter.id);
        next.set('similarName', reporter.name);
        return next;
      },
      { replace: false },
    );
  }

  function clearSimilarTo() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('similarTo');
        next.delete('similarName');
        return next;
      },
      { replace: true },
    );
  }

  // ── Saved searches ──────────────────────────────────────────────────────────
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (savedDropdownRef.current && !savedDropdownRef.current.contains(e.target as Node)) {
        setShowSavedDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function getCurrentFilters(): Record<string, unknown> {
    const filters: Record<string, unknown> = {};
    if (publicationId) { filters.publicationId = publicationId; filters.publicationName = publicationName; }
    if (beatParam) filters.beat = beatParam;
    if (hasEmail !== undefined) filters.hasEmail = hasEmail;
    if (location) filters.location = location;
    if (articleFrequency) filters.articleFrequency = articleFrequency;
    return filters;
  }

  async function handleSaveSearch() {
    const name = saveSearchName.trim();
    if (!name) { setSaveSearchError('Name is required'); return; }
    await createSavedSearch.mutateAsync({
      name,
      query: q,
      filters: getCurrentFilters(),
      searchType: 'reporter',
    });
    setShowSaveModal(false);
    setSaveSearchName('');
    setSaveSearchError('');
  }

  function applySearch(saved: SavedSearch) {
    const filters = saved.filters as Record<string, string | boolean>;
    setSearchParams(
      () => {
        const next = new URLSearchParams();
        if (saved.query) next.set('q', saved.query);
        if (filters.publicationId) { next.set('publicationId', filters.publicationId as string); next.set('publicationName', (filters.publicationName as string) ?? ''); }
        if (filters.beat) next.set('beat', filters.beat as string);
        if (filters.hasEmail !== undefined) next.set('hasEmail', String(filters.hasEmail));
        if (filters.location) next.set('location', filters.location as string);
        if (filters.articleFrequency) next.set('articleFrequency', filters.articleFrequency as string);
        return next;
      },
      { replace: false },
    );
    setSearchInput(saved.query ?? '');
    setShowSavedDropdown(false);
  }

  const hasActiveSearch = !!(q || publicationId || beatParam || hasEmail !== undefined || location || articleFrequency);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 pb-24">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-900">Reporters</h1>
        {!isLoading && !similarTo && (
          <p className="mt-1 text-sm text-gray-500">
            {total > 0
              ? `${total.toLocaleString()} reporter${total !== 1 ? 's' : ''}${q ? ` matching "${q}"` : ''}${searchMode === 'vector' ? ' — semantic search' : ''}`
              : q || activeFilterCount > 0
                ? 'No reporters match your filters'
                : 'No reporters found'}
          </p>
        )}
      </div>

      {/* Similar-to banner */}
      {similarTo && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
          <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm text-blue-800 flex-1">
            Reporters similar to <span className="font-semibold">{similarName || 'this reporter'}</span>
          </p>
          <button
            onClick={clearSimilarTo}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Search bar — hidden in similar-to mode */}
      {!similarTo && (
        <div className="mb-4">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={searchInputRef}
              type="search"
              placeholder='Search reporters — try "AI startups healthcare" or "cybersecurity enterprise"'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Search reporters"
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-mono hidden sm:block">/</kbd>
          </div>
          {q && searchMode === 'vector' && (
            <p className="mt-1.5 text-xs text-blue-600 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
              </svg>
              Semantic search active — results ranked by relevance
            </p>
          )}
        </div>
      )}

      {/* Filter row */}
      {!similarTo && (
        <div className="flex items-center gap-2 flex-wrap mb-3" role="group" aria-label="Filters">
          <PublicationCombobox
            selectedId={publicationId}
            selectedName={publicationName}
            onChange={(id, name) =>
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  if (id) { next.set('publicationId', id); next.set('publicationName', name); }
                  else { next.delete('publicationId'); next.delete('publicationName'); }
                  return next;
                },
                { replace: true },
              )
            }
          />
          <BeatFilter
            selected={selectedBeats}
            onChange={(beats) => updateFilter('beat', beats.join(',') || undefined)}
          />
          <button
            onClick={() => updateFilter('hasEmail', hasEmail === true ? undefined : 'true')}
            className={`flex items-center gap-1 h-8 px-3 text-xs border rounded-full transition-colors ${
              hasEmail === true
                ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                : 'border-gray-300 hover:border-gray-400 bg-white text-gray-700'
            }`}
            aria-pressed={hasEmail === true}
          >
            Has Email
            {hasEmail === true && <span className="text-blue-500 font-bold">✓</span>}
          </button>
          <ArticleFrequencyFilter
            value={articleFrequency}
            onChange={(v) => updateFilter('articleFrequency', v)}
          />
          {/* Location filter */}
          <LocationFilterInline
            value={location}
            onChange={(v) => updateFilter('location', v)}
          />
          {activeFilterCount > 0 && (
            <button
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    ['publicationId', 'publicationName', 'beat', 'hasEmail', 'location', 'articleFrequency'].forEach((k) => next.delete(k));
                    return next;
                  },
                  { replace: true },
                )
              }
              className="h-8 px-3 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-full hover:border-gray-400 transition-colors"
            >
              Clear filters ({activeFilterCount})
            </button>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Saved searches dropdown */}
            <div ref={savedDropdownRef} className="relative">
              <button
                onClick={() => setShowSavedDropdown((v) => !v)}
                className="flex items-center gap-1 h-8 px-3 text-xs border border-gray-300 rounded-full hover:border-gray-400 bg-white text-gray-700 transition-colors"
                title="Saved searches"
              >
                <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                Saved
                {savedSearches.length > 0 && (
                  <span className="ml-0.5 bg-blue-100 text-blue-700 text-xs font-medium px-1.5 py-0.5 rounded-full leading-none">
                    {savedSearches.length}
                  </span>
                )}
              </button>
              {showSavedDropdown && (
                <div className="absolute top-full mt-1.5 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64">
                  {savedSearches.length === 0 ? (
                    <div className="px-4 py-5 text-xs text-gray-400 text-center">No saved searches yet</div>
                  ) : (
                    <div className="max-h-72 overflow-y-auto">
                      {savedSearches.map((s) => (
                        <div
                          key={s.id}
                          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 group"
                        >
                          <button
                            onClick={() => applySearch(s)}
                            className="flex-1 text-left text-xs text-gray-800 font-medium truncate pr-2"
                          >
                            {s.name}
                            {s.query && (
                              <span className="ml-1 text-gray-400 font-normal">"{s.query}"</span>
                            )}
                          </button>
                          <button
                            onClick={() => deleteSavedSearch.mutate(s.id)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity shrink-0"
                            title="Delete"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Save current search */}
            {hasActiveSearch && (
              <button
                onClick={() => { setShowSaveModal(true); setSaveSearchName(''); setSaveSearchError(''); }}
                className="flex items-center gap-1 h-8 px-3 text-xs border border-blue-300 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Save search
              </button>
            )}
          </div>
        </div>
      )}

      {/* Save search modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-6 pt-5 pb-2">
              <h2 className="text-base font-semibold text-gray-900">Save search</h2>
              <p className="mt-1 text-xs text-gray-500">Give this search a name to find it quickly later.</p>
            </div>
            <div className="px-6 py-4">
              <input
                autoFocus
                type="text"
                placeholder="e.g. Healthcare AI reporters"
                value={saveSearchName}
                onChange={(e) => { setSaveSearchName(e.target.value); setSaveSearchError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSearch(); if (e.key === 'Escape') setShowSaveModal(false); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {saveSearchError && <p className="mt-1.5 text-xs text-red-600">{saveSearchError}</p>}
            </div>
            <div className="px-6 pb-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSearch}
                disabled={createSavedSearch.isPending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {createSavedSearch.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {!similarTo && (publicationId || selectedBeats.length > 0 || location || articleFrequency) && (
        <div className="flex items-center gap-1.5 flex-wrap mb-4">
          {publicationId && (
            <Chip
              label={publicationName}
              onRemove={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('publicationId');
                    next.delete('publicationName');
                    return next;
                  },
                  { replace: true },
                )
              }
            />
          )}
          {selectedBeats.map((beat) => (
            <Chip
              key={beat}
              label={beat}
              onRemove={() => {
                const newBeats = selectedBeats.filter((b) => b !== beat);
                updateFilter('beat', newBeats.join(',') || undefined);
              }}
            />
          ))}
          {location && <Chip label={`Location: ${location}`} onRemove={() => updateFilter('location', undefined)} />}
          {articleFrequency && <Chip label={FREQUENCY_LABELS[articleFrequency]} onRemove={() => updateFilter('articleFrequency', undefined)} />}
        </div>
      )}

      {/* Loading / error states */}
      {isLoading && (
        <div className="py-16 text-center text-sm text-gray-500">
          {similarTo ? 'Finding similar reporters…' : 'Loading reporters…'}
        </div>
      )}
      {isError && (
        <div className="py-16 text-center text-sm text-red-600">Failed to load reporters.</div>
      )}

      {/* Similar-to results */}
      {!isLoading && !isError && similarTo && (
        <>
          {similarReporters.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-500">
              No similar reporters found — try enriching this reporter's profile first.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {similarReporters.map((r) => (
                <SimilarReporterCard
                  key={r.id}
                  reporter={r}
                  onImport={(reporter) => setImportTarget(reporter)}
                  onAddToList={(reporter) => setAddToListTarget(reporter)}
                  onMoreLikeThis={(reporter) => handleMoreLikeThis(reporter)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Regular / search results */}
      {!isLoading && !isError && !similarTo && (
        <>
          {allReporters.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-gray-500">
                {q || activeFilterCount > 0
                  ? 'No reporters match your search.'
                  : 'No reporters found.'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
                <button
                  onClick={selectionMode ? handleClearSelection : handleSelectAll}
                  className="hover:text-gray-800 transition-colors"
                >
                  {selectionMode ? `${selectedIds.size} selected — clear` : 'Select all'}
                </button>
              </div>

              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                role="list"
                aria-label="Reporter search results"
              >
                {allReporters.map((reporter, i) => (
                  <ReporterCard
                    key={reporter.id}
                    reporter={reporter}
                    searchQuery={q}
                    onImport={(r) => setImportTarget(r)}
                    onAddToList={(r) => setAddToListTarget(r)}
                    onMoreLikeThis={handleMoreLikeThis}
                    selected={selectedIds.has(reporter.id)}
                    onSelectToggle={handleSelectToggle}
                    selectionMode={selectionMode}
                    focused={focusedIndex === i}
                    cardRef={(el) => { cardRefs.current[i] = el; }}
                  />
                ))}
              </div>

              <div className="mt-6 flex flex-col items-center gap-3">
                <p className="text-xs text-gray-400">
                  Showing {allReporters.length.toLocaleString()} of {total.toLocaleString()} reporters
                </p>
                {hasNextPage && (
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="px-5 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-gray-700 transition-colors"
                  >
                    {isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Floating batch action bar */}
      {selectionMode && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-full shadow-xl">
          <span className="text-sm font-medium">
            {selectedIds.size} reporter{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setShowBatchModal(true)}
            className="bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium px-4 py-1.5 rounded-full transition-colors"
          >
            Add {selectedIds.size} to Contacts
          </button>
          <button
            onClick={handleClearSelection}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Clear selection"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Modals */}
      {importTarget && (
        <ImportReporterModal reporter={importTarget} onClose={() => setImportTarget(null)} />
      )}
      {addToListTarget && (
        <AddToPressListModal reporter={addToListTarget} onClose={() => setAddToListTarget(null)} />
      )}
      {showBatchModal && selectedReporters.length > 0 && (
        <BatchImportModal
          reporters={selectedReporters}
          onClose={() => { setShowBatchModal(false); setSelectedIds(new Set()); }}
        />
      )}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
      {label}
      <button onClick={onRemove} className="text-blue-400 hover:text-blue-600 ml-0.5 text-sm leading-none">×</button>
    </span>
  );
}

function LocationFilterInline({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(value ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (value) {
    return (
      <button
        onClick={() => { onChange(undefined); setInput(''); }}
        className="flex items-center gap-1.5 h-8 px-3 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
      >
        <span className="max-w-[120px] truncate">📍 {value}</span>
        <span className="text-blue-400 hover:text-blue-600 text-sm leading-none">×</span>
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 h-8 px-3 text-xs border border-gray-300 rounded-full hover:border-gray-400 bg-white text-gray-700 transition-colors"
      >
        Location
        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1.5 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-56 p-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim()) { onChange(input.trim()); setOpen(false); }
            }}
            className="flex gap-2"
          >
            <input
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. New York, SF"
              className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400"
            />
            <button
              type="submit"
              className="px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Go
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
